import { useState } from 'react';
import {
    Users,
    Crown,
    MoreVertical,
    UserMinus,
    Shield,
    ShieldOff,
    Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceMembers } from '@/hooks/useWorkspaceMembers';
import { useAuth } from '@/contexts/AuthContext';
import type { WorkspaceMemberWithUser, WorkspaceRole } from '@/types/workspace';
import { cn } from '@/lib/utils';

export function WorkspaceMembers() {
    const { user } = useAuth();
    const { isAdmin } = useWorkspace();
    const { members, loading, error, removeMember, updateRole } = useWorkspaceMembers();

    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        type: 'remove' | 'role';
        member: WorkspaceMemberWithUser | null;
        newRole?: WorkspaceRole;
    }>({
        open: false,
        type: 'remove',
        member: null,
    });

    const handleRemoveMember = async () => {
        if (!confirmDialog.member) return;

        setActionLoading(confirmDialog.member.userId);
        try {
            await removeMember(confirmDialog.member.userId);
        } finally {
            setActionLoading(null);
            setConfirmDialog({ open: false, type: 'remove', member: null });
        }
    };

    const handleUpdateRole = async () => {
        if (!confirmDialog.member || !confirmDialog.newRole) return;

        setActionLoading(confirmDialog.member.userId);
        try {
            await updateRole(confirmDialog.member.userId, confirmDialog.newRole);
        } finally {
            setActionLoading(null);
            setConfirmDialog({ open: false, type: 'role', member: null });
        }
    };

    const openRemoveDialog = (member: WorkspaceMemberWithUser) => {
        setConfirmDialog({
            open: true,
            type: 'remove',
            member,
        });
    };

    const openRoleDialog = (member: WorkspaceMemberWithUser, newRole: WorkspaceRole) => {
        setConfirmDialog({
            open: true,
            type: 'role',
            member,
            newRole,
        });
    };

    const closeDialog = () => {
        setConfirmDialog({ open: false, type: 'remove', member: null });
    };

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Team Members
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                                <div className="size-10 rounded-full bg-muted animate-pulse" />
                                <div className="flex-1">
                                    <div className="h-4 w-32 bg-muted rounded animate-pulse mb-2" />
                                    <div className="h-3 w-48 bg-muted rounded animate-pulse" />
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Team Members
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
                        {error}
                    </div>
                </CardContent>
            </Card>
        );
    }

    const admins = members.filter((m: any) => m.role === 'admin');
    const regularMembers = members.filter((m: any) => m.role === 'member');

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Team Members
                    </CardTitle>
                    <CardDescription>
                        {members.length} member{members.length !== 1 ? 's' : ''} in this workspace
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Admins */}
                    {admins.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                <Crown className="h-4 w-4" />
                                Admins ({admins.length})
                            </h4>
                            <div className="space-y-2">
                                {admins.map((member: any) => (
                                    <MemberRow
                                        key={member.id}
                                        member={member}
                                        isCurrentUser={member.userId === user?.id}
                                        isAdmin={isAdmin}
                                        isOnlyAdmin={admins.length === 1}
                                        isLoading={actionLoading === member.userId}
                                        onRemove={() => openRemoveDialog(member)}
                                        onChangeRole={(role) => openRoleDialog(member, role)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Members */}
                    {regularMembers.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-muted-foreground">
                                Members ({regularMembers.length})
                            </h4>
                            <div className="space-y-2">
                                {regularMembers.map((member: any) => (
                                    <MemberRow
                                        key={member.id}
                                        member={member}
                                        isCurrentUser={member.userId === user?.id}
                                        isAdmin={isAdmin}
                                        isOnlyAdmin={false}
                                        isLoading={actionLoading === member.userId}
                                        onRemove={() => openRemoveDialog(member)}
                                        onChangeRole={(role) => openRoleDialog(member, role)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.open} onOpenChange={(open: boolean) => !open && closeDialog()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {confirmDialog.type === 'remove'
                                ? 'Remove Member'
                                : `Change Role to ${confirmDialog.newRole === 'admin' ? 'Admin' : 'Member'}`}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmDialog.type === 'remove'
                                ? `Are you sure you want to remove ${confirmDialog.member?.user.name || confirmDialog.member?.user.email} from this workspace? They will lose access to all workspace resources.`
                                : `Are you sure you want to change ${confirmDialog.member?.user.name || confirmDialog.member?.user.email}'s role to ${confirmDialog.newRole}?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDialog.type === 'remove' ? handleRemoveMember : handleUpdateRole}
                            variant={confirmDialog.type === 'remove' ? 'destructive' : 'default'}
                        >
                            {confirmDialog.type === 'remove' ? 'Remove' : 'Change Role'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

interface MemberRowProps {
    member: WorkspaceMemberWithUser;
    isCurrentUser: boolean;
    isAdmin: boolean;
    isOnlyAdmin: boolean;
    isLoading: boolean;
    onRemove: () => void;
    onChangeRole: (role: WorkspaceRole) => void;
}

function MemberRow({
    member,
    isCurrentUser,
    isAdmin,
    isOnlyAdmin,
    isLoading,
    onRemove,
    onChangeRole,
}: MemberRowProps) {
    const canManage = isAdmin && !isCurrentUser;
    const canChangeRole = canManage && !(member.role === 'admin' && isOnlyAdmin);
    const canRemove = canManage && !(member.role === 'admin' && isOnlyAdmin);

    return (
        <div className={cn(
            "flex items-center gap-3 p-3 rounded-lg transition-colors",
            "bg-muted/30 hover:bg-muted/50"
        )}>
            {/* Avatar */}
            <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                    {(member.user.name || member.user.email).charAt(0).toUpperCase()}
                </span>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                        {member.user.name || member.user.email.split('@')[0]}
                    </span>
                    {isCurrentUser && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            You
                        </span>
                    )}
                    {member.role === 'admin' && (
                        <Crown className="h-3.5 w-3.5 text-amber-500" />
                    )}
                </div>
                <p className="text-sm text-muted-foreground truncate">
                    {member.user.email}
                </p>
            </div>

            {/* Role Badge */}
            <span className={cn(
                "px-2 py-1 rounded text-xs font-medium",
                member.role === 'admin'
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                    : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            )}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </span>

            {/* Actions */}
            {canManage && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <MoreVertical className="h-4 w-4" />
                            )}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {canChangeRole && (
                            <>
                                {member.role === 'member' ? (
                                    <DropdownMenuItem onClick={() => onChangeRole('admin')}>
                                        <Shield className="h-4 w-4 mr-2" />
                                        Make Admin
                                    </DropdownMenuItem>
                                ) : (
                                    <DropdownMenuItem onClick={() => onChangeRole('member')}>
                                        <ShieldOff className="h-4 w-4 mr-2" />
                                        Remove Admin
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {canRemove && (
                            <DropdownMenuItem
                                onClick={onRemove}
                                className="text-destructive focus:text-destructive"
                            >
                                <UserMinus className="h-4 w-4 mr-2" />
                                Remove Member
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
}
